import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  AttachMoney,
  Speed,
  VolumeUp,
  Settings,
  Info
} from '@mui/icons-material';

function ProviderConfiguration({ configuration, providers, onConfigChange }) {
  if (!configuration) return null;

  const handleProviderToggle = (providerName, enabled) => {
    const updatedConfig = {
      ...configuration,
      providers: {
        ...configuration.providers,
        [providerName]: {
          ...configuration.providers[providerName],
          enabled
        }
      }
    };
    onConfigChange(updatedConfig);
  };

  const handlePriorityChange = (providerName, priority) => {
    const updatedConfig = {
      ...configuration,
      providers: {
        ...configuration.providers,
        [providerName]: {
          ...configuration.providers[providerName],
          priority: parseInt(priority, 10)
        }
      }
    };
    onConfigChange(updatedConfig);
  };

  const getProviderStatus = (providerName) => {
    const provider = providers.find(p => p.name === providerName);
    return provider?.available || false;
  };

  const getProviderCapabilities = (providerName) => {
    const provider = providers.find(p => p.name === providerName);
    return provider?.capabilities || {};
  };

  const providerDetails = {
    elevenlabs: {
      displayName: 'ElevenLabs',
      description: 'Premium voice synthesis with highest quality',
      icon: '🎯',
      color: '#6366f1'
    },
    google: {
      displayName: 'Google Cloud TTS',
      description: 'Cost-effective with good British voices',
      icon: '🔊',
      color: '#4285f4'
    },
    azure: {
      displayName: 'Azure Cognitive Services',
      description: 'Advanced neural voices with emotion support',
      icon: '☁️',
      color: '#0078d4'
    },
    openai: {
      displayName: 'OpenAI TTS',
      description: 'Simple and reliable text-to-speech',
      icon: '🤖',
      color: '#10a37f'
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Provider Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Enable and configure TTS providers. Lower priority numbers are preferred.
      </Typography>

      <Grid container spacing={3}>
        {Object.entries(configuration.providers || {}).map(([providerName, providerConfig]) => {
          const details = providerDetails[providerName] || {};
          const isAvailable = getProviderStatus(providerName);
          const capabilities = getProviderCapabilities(providerName);
          
          return (
            <Grid item xs={12} md={6} key={providerName}>
              <Card 
                variant="outlined"
                sx={{ 
                  borderColor: providerConfig.enabled ? details.color : 'divider',
                  borderWidth: providerConfig.enabled ? 2 : 1
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6" component="div">
                        <span style={{ marginRight: 8 }}>{details.icon}</span>
                        {details.displayName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {details.description}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={providerConfig.enabled}
                          onChange={(e) => handleProviderToggle(providerName, e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enabled"
                    />
                  </Box>

                  <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      icon={isAvailable ? <CheckCircle /> : <Cancel />}
                      label={isAvailable ? 'Available' : 'Not Available'}
                      color={isAvailable ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<AttachMoney />}
                      label={`$${providerConfig.costPer1000Chars}/1k chars`}
                      size="small"
                      variant="outlined"
                    />
                    {capabilities.supportsStreaming && (
                      <Chip
                        icon={<Speed />}
                        label="Streaming"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {capabilities.supportsSSML && (
                      <Chip
                        icon={<VolumeUp />}
                        label="SSML"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {providerConfig.enabled && (
                    <Box mt={2}>
                      <TextField
                        label="Priority"
                        type="number"
                        value={providerConfig.priority}
                        onChange={(e) => handlePriorityChange(providerName, e.target.value)}
                        InputProps={{
                          inputProps: { min: 1, max: 10 }
                        }}
                        size="small"
                        sx={{ width: 100 }}
                      />
                    </Box>
                  )}

                  {capabilities.supportedLanguages && (
                    <Box mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        Languages: {capabilities.supportedLanguages.slice(0, 5).join(', ')}
                        {capabilities.supportedLanguages.length > 5 && ` +${capabilities.supportedLanguages.length - 5} more`}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Cost Comparison
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Monthly Cost Projection (10k users)"
                  secondary="Based on average 16M characters/month"
                />
              </ListItem>
              <Divider />
              {Object.entries(configuration.providers || {}).map(([providerName, providerConfig]) => {
                const details = providerDetails[providerName] || {};
                const monthlyCost = (16000 * providerConfig.costPer1000Chars).toFixed(2);
                const savings = providerName !== 'elevenlabs' 
                  ? ((1 - (providerConfig.costPer1000Chars / 0.18)) * 100).toFixed(0)
                  : 0;
                
                return (
                  <ListItem key={providerName}>
                    <ListItemIcon>
                      <span>{details.icon}</span>
                    </ListItemIcon>
                    <ListItemText 
                      primary={details.displayName}
                      secondary={`$${monthlyCost}/month`}
                    />
                    {savings > 0 && (
                      <Chip 
                        label={`${savings}% savings`} 
                        color="success" 
                        size="small" 
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default ProviderConfiguration;